import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { milestonesApi } from '@/services/api/milestones';
import type { Database } from '@/types/database.types';

type MilestoneInsert = Database['public']['Tables']['milestones']['Insert'];
type MilestoneUpdate = Database['public']['Tables']['milestones']['Update'];

/**
 * Hook to fetch all milestones for a specific project
 */
export const useMilestones = (projectId: number | null) => {
  return useQuery({
    queryKey: ['milestones', projectId],
    queryFn: async () => {
      const { data, error } = await milestonesApi.getByProjectId(projectId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single milestone by ID
 */
export const useMilestone = (id: number | null) => {
  return useQuery({
    queryKey: ['milestones', 'detail', id],
    queryFn: async () => {
      const { data, error } = await milestonesApi.getById(id!);
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });
};

/**
 * Hook to create a new milestone
 */
export const useCreateMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (milestone: MilestoneInsert) => {
      const { data, error } = await milestonesApi.create(milestone);
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data?.project_id) {
        queryClient.invalidateQueries({ queryKey: ['milestones', data.project_id] });
      }
    },
  });
};

/**
 * Hook to update an existing milestone
 */
export const useUpdateMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: MilestoneUpdate }) => {
      const { data, error } = await milestonesApi.update(id, updates);
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data?.project_id) {
        queryClient.invalidateQueries({ queryKey: ['milestones', data.project_id] });
        queryClient.invalidateQueries({ queryKey: ['milestones', 'detail', data.id] });
      }
    },
  });
};

/**
 * Hook to delete a milestone.
 * Requires projectId from the caller — avoids relying on stale detail cache
 * which may not be populated if the user never opened the detail view.
 */
export const useDeleteMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: number; projectId: number }) => {
      const { error } = await milestonesApi.delete(id);
      if (error) throw error;
      return projectId;
    },
    onSuccess: (projectId) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
    },
  });
};

/** Fetch all milestones across multiple projects (for dashboard export). */
export const useAllMilestones = (projectIds: number[]) => {
  return useQuery({
    queryKey: ['milestones', 'all', projectIds],
    queryFn: async () => {
      const { data, error } = await milestonesApi.getByProjectIds(projectIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
